import { db } from "helpers/firebase/firebaseConfig";
import { collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";
import Cookies from "js-cookie";
import { actionTypes } from "./actions";
import { call, put, takeEvery } from 'redux-saga/effects';

async function fetchInvoices() {
  try {
    const userId = Cookies.get("usersessionId", {
      secure: true,
      sameSite: "strict",
      path: "/",
    });

    if (!userId) throw new Error("User ID not found");

    // 1. Fetch invoices for this provider
    const invoicesQuery = query(
      collection(db, "service_provider_sale_invoices"),
      where("serviceProviders", "==", userId)
    );
    const invoicesSnapshot = await getDocs(invoicesQuery);

    // 2. Collect related IDs
    const bookingIds = new Set();
    const providerIds = new Set();
    const userIds = new Set();
    const serviceIds = new Set();

    invoicesSnapshot.forEach(doc => {
      const invoice = doc.data();
      if (invoice.booking) bookingIds.add(invoice.booking);
      if (invoice.serviceProviders) providerIds.add(invoice.serviceProviders);
    });

    // 3. Fetch bookings
    const bookingDocs =
      bookingIds.size > 0
        ? await getDocs(
            query(
              collection(db, "Booking"),
              where("__name__", "in", Array.from(bookingIds))
            )
          )
        : { docs: [] };

    const bookingsMap = new Map();
    bookingDocs.docs.forEach(bookingDoc => {
      const booking = bookingDoc.data();
      bookingsMap.set(bookingDoc.id, booking);
      if (booking.app_user_id) userIds.add(booking.app_user_id);
      if (booking.serviceProviderServices)
        serviceIds.add(booking.serviceProviderServices);
    });

    // 4. Fetch service providers
    const providersSnap = await Promise.all(
      Array.from(providerIds).map(id => getDoc(doc(db, "service_providers", id)))
    );
    const providersMap = new Map(
      providersSnap.filter(d => d.exists()).map(d => [d.id, d.data()])
    );

    // 5. Fetch app users
    const userDocs =
      userIds.size > 0
        ? await getDocs(
            query(
              collection(db, "app_users"),
              where("__name__", "in", Array.from(userIds))
            )
          )
        : { docs: [] };
    const usersMap = new Map(userDocs.docs.map(d => [d.id, d.data()]));

    // 6. Fetch serviceProviderServices
    const servicesDocs =
      serviceIds.size > 0
        ? await getDocs(
            query(
              collection(db, "service_provider_services"),
              where("__name__", "in", Array.from(serviceIds))
            )
          )
        : { docs: [] };
    const servicesMap = new Map(servicesDocs.docs.map(d => [d.id, d.data()]));

    // 7. Enrich and return invoices
    const enrichedInvoices = invoicesSnapshot.docs.map(doc => {
      const invoice = doc.data();
      const bookingData = bookingsMap.get(invoice.booking) || null;

      const userData = bookingData?.app_user_id
        ? usersMap.get(bookingData.app_user_id) || null
        : null;

      const serviceData = bookingData?.serviceProviderServices
        ? servicesMap.get(bookingData.serviceProviderServices) || null
        : null;

      return {
        id: doc.id,
        ...invoice,
        serviceProviders: providersMap.get(invoice.serviceProviders) || null,
        booking: bookingData
          ? {
              ...bookingData,
              app_user_id: userData,
              serviceProviderServices: serviceData,
            }
          : null,
      };
    });

    return enrichedInvoices;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
}

function* fetchTransactionsSaga() {
  const respon = yield call(fetchInvoices);
  yield put({ type: actionTypes.SET_TRANSACTIONS, data: respon });
}

async function fetchTotalRevenueForProvider() {
  try {
    const userId = Cookies.get("usersessionId", {
      secure: true,
      sameSite: "strict",
      path: "/",
    });

    if (!userId) throw new Error("User ID not found");

    // Fetch only invoices for this provider
    const invoicesQuery = query(
      collection(db, "total_revenue"),
      where("service_provider_id", "==", userId)
    );
    
    const invoicesSnapshot = await getDocs(invoicesQuery);

    // Extract total revenue from the single record
    const invoice = invoicesSnapshot.docs[0]?.data();
    const totalRevenue = invoice?.amount ? parseFloat(invoice.amount) : 0;

    return totalRevenue;
  } catch (error) {
    console.error("Error calculating total revenue:", error);
    throw error;
  }
}

function* fetchTotalRevenueSaga() {
  try {
    const totalRevenue = yield call(fetchTotalRevenueForProvider);

    yield put({ type: actionTypes.SET_TOTAL_REVENUE, amount: totalRevenue });
  } catch (error) {
    console.error("Error in fetchTotalRevenueSaga:", error);
  }
}

export default function* rootSaga() {
  yield takeEvery(actionTypes.FETCH_TRANSACTIONS, fetchTransactionsSaga);
  yield takeEvery(actionTypes.FETCH_TOTAL_REVENUE, fetchTotalRevenueSaga);
}

